import { STATIC_HOST } from "@/config/api";
import { prefetchChapter } from "@/services/chapterService";
import { clientRequestQueue } from "@/services/requestQueue";

let activeRunToken = 0;
let idleHandle = null;
let usingIdleCallback = false;

function scheduleIdle(cb) {
  if (typeof window !== "undefined" && typeof window.requestIdleCallback === "function") {
    usingIdleCallback = true;
    idleHandle = window.requestIdleCallback(cb, { timeout: 1500 });
  } else {
    usingIdleCallback = false;
    idleHandle = setTimeout(cb, 300);
  }
}

function clearScheduledIdle() {
  if (idleHandle === null) return;
  if (usingIdleCallback && typeof window !== "undefined" && typeof window.cancelIdleCallback === "function") {
    window.cancelIdleCallback(idleHandle);
  } else {
    clearTimeout(idleHandle);
  }
  idleHandle = null;
}

export function canPrefetch() {
  if (typeof document !== "undefined" && document.visibilityState === "hidden") {
    return false;
  }
  if (typeof navigator !== "undefined") {
    if (navigator.onLine === false) {
      return false;
    }
    const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (conn && conn.saveData) {
      return false;
    }
  }
  return clientRequestQueue.isUserIdle();
}

function getPrefetchDepth() {
  if (typeof navigator !== "undefined") {
    const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    const effectiveType = conn?.effectiveType || "";
    if (effectiveType === "slow-2g" || effectiveType === "2g" || effectiveType === "3g") {
      return 1;
    }
  }
  return 2;
}

function injectLegacyLinkPrefetch(htmlURL) {
  if (!htmlURL || typeof document === "undefined" || !document.head) return;
  const href = `${STATIC_HOST}${htmlURL}`;
  if (document.head.querySelector(`link[rel="prefetch"][href="${href}"]`)) {
    return;
  }
  const link = document.createElement("link");
  link.rel = "prefetch";
  link.href = href;
  link.as = "document";
  document.head.appendChild(link);
}

/**
 * Cancel any pending or in-flight scheduled prefetch sequence.
 */
export function cancelPrefetch() {
  activeRunToken += 1;
  clearScheduledIdle();
}

/**
 * Schedule predictive prefetching for N+1 (with content), N+2 (with content if depth=2),
 * and N-1 (metadata only) once the browser and user queue are idle.
 * @param {object} currentChapter
 * @param {object} book
 * @param {Function} [onLog]
 */
export function scheduleChapterPrefetch(currentChapter, book, onLog) {
  cancelPrefetch();
  if (!currentChapter) return;

  const token = activeRunToken;

  scheduleIdle(async () => {
    if (token !== activeRunToken || !canPrefetch()) {
      return;
    }

    const depth = getPrefetchDepth();

    // 1. Prefetch N+1 with content
    const nextUrl = currentChapter.forwardAbsUrl;
    let nextChapter = null;
    if (nextUrl && token === activeRunToken && canPrefetch()) {
      if (onLog) onLog("Preloading next chapter (N+1)...", "log-info");
      nextChapter = await prefetchChapter(nextUrl, { book, withContent: true });
      if (nextChapter?.htmlURL && !nextChapter?.html) {
        injectLegacyLinkPrefetch(nextChapter.htmlURL);
      }
      if (nextChapter && token === activeRunToken && onLog) {
        onLog("Next chapter (N+1) is ready!", "log-success");
      }
    }

    // 2. Prefetch N+2 using N+1's forwardAbsUrl
    if (
      depth >= 2 &&
      nextChapter?.forwardAbsUrl &&
      token === activeRunToken &&
      canPrefetch()
    ) {
      const nPlus2 = await prefetchChapter(nextChapter.forwardAbsUrl, {
        book,
        withContent: true,
      });
      if (nPlus2?.htmlURL && !nPlus2?.html) {
        injectLegacyLinkPrefetch(nPlus2.htmlURL);
      }
    }

    // 3. Prefetch N-1 (metadata only)
    const prevUrl = currentChapter.backAbsUrl;
    if (prevUrl && token === activeRunToken && canPrefetch()) {
      await prefetchChapter(prevUrl, { book, withContent: false });
    }
  });
}
